import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderKanban, ClipboardCheck, FileCheck2, MessageSquare, AlertTriangle,
  ArrowUpRight, Rocket, PencilLine, Clock, Command,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusPill, SectionLabel } from "@/components/Common";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

function Metric({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="kp-card p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="font-display text-4xl text-foreground tabular-nums leading-none">{value}</p>
      <p className="text-sm text-muted-foreground mt-2">{label}</p>
    </div>
  );
}

export default function ConsoleOverview() {
  const { user, setActiveProgram } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/console/overview").then((r) => setD(r.data)).catch(() => setError(true));
  }, []);

  if (error) return <EmptyState icon={AlertTriangle} title="Unable to load the console overview" hint="Please try again later." />;
  if (!d) return <div className="text-muted-foreground kp-fade">Loading console…</div>;

  const t = d.totals;
  const programName = (id) => d.programs.find((p) => p.id === id)?.name || id;
  const openWorkspace = (pid) => { setActiveProgram(pid); navigate("/app/dashboard"); };

  return (
    <div>
      <PageHeader
        eyebrow={<span className="flex items-center gap-1.5"><Command className="w-3 h-3" /> Console · Overview</span>}
        title={`Welcome back, ${user?.name?.split(" ")[0]}.`}
        subtitle={d.scope === "all"
          ? "Cross-program control centre — every program, in one place."
          : "Cross-program control centre — the programs you own."}
        testid="console-overview-header" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-12 stagger" data-testid="console-metrics">
        <Metric icon={FolderKanban}   label="Programs in scope"   value={t.programs}       tone="bg-secondary/70 text-primary" testid="metric-programs" />
        <Metric icon={ClipboardCheck} label="Awaiting your sign-off" value={t.pending_for_me} tone="bg-[#fbe0d9] text-[#a23a2a]" testid="metric-pending" />
        <Metric icon={FileCheck2}     label="Published documents" value={t.published}      tone="bg-[#d9f2e5] text-[#25604d]" testid="metric-published" />
        <Metric icon={PencilLine}     label="Drafts in progress"  value={t.drafts}         tone="bg-muted text-foreground" testid="metric-drafts" />
        <Metric icon={MessageSquare}  label="Assistant queries"   value={t.queries}        tone="bg-accent/40 text-primary" testid="metric-queries" />
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <SectionLabel>Programs</SectionLabel>
          {d.programs.length === 0 ? (
            <EmptyState icon={FolderKanban} title="No programs in your scope" hint="An administrator needs to assign you a program." />
          ) : (
            <div className="space-y-4 stagger" data-testid="console-program-list">
              {d.programs.map((p) => (
                <div key={p.id} className="kp-card kp-card-lift p-6" data-testid={`console-program-${p.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display text-xl text-foreground leading-tight">{p.name}</h3>
                      <p className="text-[11px] font-mono uppercase tracking-wider text-primary mt-1">
                        {p.jira_tag} · PM {p.pm_name}
                      </p>
                    </div>
                    {p.pending_for_me > 0 && (
                      <Badge variant="outline" className="bg-[#fbe0d9] text-[#a23a2a] border-[#f0b8a8] font-medium" data-testid={`pending-badge-${p.id}`}>
                        {p.pending_for_me} awaiting you
                      </Badge>
                    )}
                    <button onClick={() => openWorkspace(p.id)} data-testid={`open-workspace-${p.id}`}
                      className="text-xs font-semibold text-primary flex items-center gap-1 kp-link">
                      Open workspace <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 text-center">
                    {[["Published", p.published, "text-[#25604d]"], ["In review", p.submitted, "text-[#7a5a12]"],
                      ["Drafts", p.draft, "text-foreground"], ["Rejected", p.rejected, "text-destructive"]].map(([label, v, cls]) => (
                      <div key={label} className="rounded-xl bg-background border border-border py-2.5">
                        <p className={`font-display text-2xl tabular-nums leading-none ${cls}`}>{v}</p>
                        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">{label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 flex items-center gap-3">
                    <Progress value={p.checklist_total ? (p.checklist_done / p.checklist_total) * 100 : 100} className="h-2 flex-1" />
                    <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground shrink-0 flex items-center gap-1">
                      {p.checklist_total && p.checklist_done === p.checklist_total
                        ? <><Rocket className="w-3 h-3 text-[#25604d]" /> Checklist ready</>
                        : <>{p.checklist_done}/{p.checklist_total} steps</>}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <SectionLabel>Recent activity</SectionLabel>
          <div className="kp-card divide-y divide-border overflow-hidden" data-testid="console-activity">
            {d.recent_activity.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground italic">No activity yet.</p>}
            {d.recent_activity.map((a, i) => (
              <div key={i} className="p-4">
                <p className="text-sm font-semibold text-foreground truncate">{a.title}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <StatusPill status={a.status} />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground truncate flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {programName(a.program_id)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {t.unanswered > 0 && (
            <div className="mt-5 rounded-2xl border border-[#f0d98c] bg-[#fdf2c9] p-5" data-testid="console-gap-alert">
              <p className="font-display text-lg text-[#5c4310] flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {t.unanswered} unanswered questions
              </p>
              <p className="text-xs text-[#7a5a12] mt-1">Users asked things your knowledge base can't answer yet.</p>
              <button onClick={() => navigate("/manage/analytics")} className="text-xs font-semibold text-[#5c4310] kp-link mt-3" data-testid="view-gaps">
                Review knowledge gaps →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
